import React, { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';

interface SuccessMessageProps {
  darkMode?: boolean;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({ darkMode = false }) => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Show confetti for 4 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const confettiPieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][i % 6],
    delay: Math.random() * 3,
    duration: 3 + Math.random() * 2,
    left: Math.random() * 100,
    size: 3 + Math.random() * 5,
    rotation: Math.random() * 360,
    drift: (Math.random() - 0.5) * 100 // Horizontal drift
  }));

  return (
    <div className={`relative rounded-xl p-6 text-center overflow-hidden ${
      darkMode 
        ? 'bg-green-900/30 border border-green-400/30 backdrop-blur-sm' 
        : 'bg-green-50/90 border border-green-200 backdrop-blur-sm'
    }`}>
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {confettiPieces.map((piece) => (
            <div
              key={piece.id}
              className="absolute -top-2"
              style={{
                left: `${piece.left}%`,
                animation: `confettiFall ${piece.duration}s ease-out ${piece.delay}s forwards`,
                '--drift': `${piece.drift}px`,
                '--rotation': `${piece.rotation}deg`
              } as React.CSSProperties & { '--drift': string; '--rotation': string }}
            >
              <div
                className="rounded-full"
                style={{
                  backgroundColor: piece.color,
                  width: `${piece.size}px`,
                  height: `${piece.size}px`,
                  animation: `confettiRotate ${piece.duration}s linear infinite`
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Success Content */}
      <div className="relative z-10">
        <div className="flex items-center justify-center mb-4">
          <div className={`w-12 h-12 ${darkMode ? 'bg-green-500' : 'bg-green-500'} rounded-full flex items-center justify-center ${showConfetti ? 'animate-pulse' : ''}`}>
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
        </div>
        
        <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-green-400' : 'text-green-900'}`}>
          🎉 Thanks for joining!
        </h3>
        
        <p className={`text-base leading-relaxed ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
          You're all set! We'll notify you as soon as ContaX is ready to help you build stronger professional relationships.
        </p>
        
        <div className={`mt-4 text-sm ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>You're on the waitlist</span>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(-10px) translateX(0px) rotateZ(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(300px) translateX(var(--drift)) rotateZ(var(--rotation));
            opacity: 0;
          }
        }

        @keyframes confettiRotate {
          0% {
            transform: rotateZ(0deg);
          }
          100% {
            transform: rotateZ(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default SuccessMessage;